
import React, { createContext, useContext } from "react";
import { AuthContextType, User } from "./auth/types";
import { useAuthState } from "./auth/useAuthState";
import { loginUser, logoutUser } from "./auth/authOperations";
import { hybridLoginUser, hybridLogoutUser } from "./auth/hybridAuthOperations";
import { savePackerRelations } from "./auth/authUtils";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    user, 
    setUser, 
    isAuthenticated, 
    setIsAuthenticated, 
    selectedPackers, 
    setSelectedPackers 
  } = useAuthState();

  const updateSelectedPackers = (packers: string[]) => {
    localStorage.setItem('selectedPackers', JSON.stringify(packers));
    setSelectedPackers(packers);
    
    // If a user is logged in, update the packer relations in the database
    if (user) {
      console.log("Updating packer relations for user:", user.id, "name:", user.name, "packers:", packers);
      savePackerRelations(user.id, packers, user.name);
    }
  };

  const login = async (username: string, password: string, packers: string[] = []): Promise<boolean> => {
    return hybridLoginUser(username, password, packers, setUser, setIsAuthenticated, setSelectedPackers);
  };

  const logout = () => {
    hybridLogoutUser(user, setUser, setIsAuthenticated, setSelectedPackers);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated, 
      selectedPackers, 
      updateSelectedPackers 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Re-export types for convenience
export type { UserRole, User } from "./auth/types";
